import { HAS_AMOUNT, HISTORY_KINDS, HISTORY_KIND_WORDS, titleCase, type HistoryEntryDto, type HistoryKind, type ProcessStepDto } from '@nsb/core';
import { useContext, useId, useState } from 'react';
import { Link } from 'react-router';
import { api, errorText, queryClient, upload } from '../../api.ts';
import { dateWords, easternToday } from '../../days.ts';
import { DropZone } from '../DropZone.tsx';
import { MoneyInput } from '../MoneyInput.tsx';
import { Button, ErrorLine, Seg, TextInput, Toggle } from '../ui.tsx';
import { useUnsaved } from '../useUnsaved.ts';
import { changedLabels } from '../../unsaved.ts';
import { latestQuote, quoteWords } from './stepLog.ts';
import { LogCounts } from './useStepLog.ts';

/**
 * A log on every step (design study 31, slice 3; Jason, 2026-10-29: "each node requires work, but also generates next
 * step work, and has various assets and completed work associated with it"): dated entries on its working panel,
 * newest first, its tasks' with it, each a day and a kind beside its words, its task and its files; NSB's unless
 * shared. Built on the project's dated entries, so each is on the timeline too, its files in Files.
 */

/** A task's row: how many entries its log has, pressed to read them. */
export function TaskLogCount({ taskId }: { taskId: string }) {
  const log = useContext(LogCounts);
  const n = log?.counts.get(taskId) ?? 0;
  if (!log || !n) return null;
  return (
    <button type="button" className="link hit ml-2 text-ink-2" onClick={() => log.show(taskId)}>
      {n} in the log
    </button>
  );
}

/** The latest quote on the step or one of its tasks, at the top of its panel. */
export function LatestQuote({ projectId, entries }: { projectId: string; entries: HistoryEntryDto[] }) {
  const q = latestQuote(entries);
  if (!q) return null;
  return (
    <p className="mt-2 text-sm">
      {quoteWords(q)}
      {q.files.map((f) => (
        <FileChip key={f.id} projectId={projectId} file={f} />
      ))}
      <OnRecord url={q.sourceUrl} />
    </p>
  );
}

/** Where an entry is on record (a quote carried in from the step keeps its link there): NSB's alone. */
function OnRecord({ url }: { url: string | null }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="link ml-2 text-[0.8125rem]">
      On record
    </a>
  );
}

/** A file of an entry, opened on its page in Files: from NSB's log and from a client's card alike (L-36). */
export function FileChip({ projectId, file }: { projectId: string; file: { id: string; name: string } }) {
  return (
    <Link to={`/p/${projectId}/reviews/files/${file.id}?from=files`} className="link ml-2 text-[0.8125rem]">
      {file.name}
    </Link>
  );
}

/** The Log block: its entries, newest first (a task's alone when its count was pressed), and the line to add one. */
export function StepLog({
  projectId,
  step,
  tasks,
  entries,
  forTaskId,
  onShowAll,
}: {
  projectId: string;
  step: ProcessStepDto;
  tasks: ProcessStepDto[];
  entries: HistoryEntryDto[];
  forTaskId: string | null;
  onShowAll: () => void;
}) {
  const shown = forTaskId ? entries.filter((e) => e.step?.id === forTaskId) : entries;
  const task = forTaskId ? tasks.find((t) => t.id === forTaskId) : undefined;
  return (
    <div className="border-t border-rule py-3">
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <span className="label">Log</span>
        <span className="meta">NSB’s unless shared</span>
      </div>
      {task ? (
        <p className="meta mb-1">
          {titleCase(task.title)}’s alone ·{' '}
          <button type="button" className="link hit" onClick={onShowAll}>
            Show the whole step
          </button>
        </p>
      ) : null}
      {shown.length ? (
        <ul className="divide-y divide-rule">
          {shown.map((e) => (
            <LogRow key={e.id} projectId={projectId} step={step} tasks={tasks} entry={e} />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-ink-3">Nothing written down on it yet.</p>
      )}
      <LogForm projectId={projectId} step={step} tasks={tasks} onDone={() => {}} />
    </div>
  );
}

function LogRow({ projectId, step, tasks, entry: e }: { projectId: string; step: ProcessStepDto; tasks: ProcessStepDto[]; entry: HistoryEntryDto }) {
  const [open, setOpen] = useState(false);
  const onTask = e.step && e.step.id !== step.id ? e.step.name : null;
  if (open) {
    return (
      <li className="py-2">
        <LogForm projectId={projectId} step={step} tasks={tasks} entry={e} onDone={() => setOpen(false)} />
      </li>
    );
  }
  return (
    <li className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-3 py-2 text-sm">
      <span className="font-mono text-[11px] uppercase leading-5 tracking-[0.06em] text-ink-3">
        {dateWords(e.happenedOn)}
        <br />
        {HISTORY_KIND_WORDS[e.kind]}
      </span>
      <span className="min-w-0">
        {e.title}
        {onTask ? <span className="text-ink-3"> · {onTask}</span> : null}
        {e.body ? <span className="block whitespace-pre-line text-ink-2">{e.body}</span> : null}
        {HAS_AMOUNT.includes(e.kind) && (e.amountCents !== null || e.whoFrom) ? (
          <span className="meta block">{[e.amountCents !== null ? quoteAmount(e.amountCents) : null, e.whoFrom ? `from ${e.whoFrom}` : null].filter(Boolean).join(' ')}</span>
        ) : null}
        {e.files.length || e.sourceUrl ? (
          <span className="block">
            {e.files.map((f) => (
              <FileChip key={f.id} projectId={projectId} file={f} />
            ))}
            <OnRecord url={e.sourceUrl} />
          </span>
        ) : null}
        <span className="meta block">
          {e.shared ? 'Shared with the client' : 'NSB’s'} ·{' '}
          <button type="button" className="link hit" onClick={() => setOpen(true)}>
            Change it
          </button>
        </span>
      </span>
    </li>
  );
}

const quoteAmount = (cents: number) => `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

/**
 * An entry written down on the step, or changed: its kind (Note first), what happened, its files, its day, the step or
 * which of its tasks it is for, and whether the client reads it; a quote's or an invoice's amount and who it is from.
 * Its files go up only as it is added, into the project's files, named for the log when they come named by an id.
 */
export function LogForm({
  projectId,
  step,
  tasks,
  entry,
  onDone,
}: {
  projectId: string;
  step: ProcessStepDto;
  tasks: ProcessStepDto[];
  entry?: HistoryEntryDto;
  onDone: () => void;
}) {
  const id = useId();
  const [kind, setKind] = useState<HistoryKind>(entry?.kind ?? 'note');
  const [title, setTitle] = useState(entry?.title ?? '');
  const [body, setBody] = useState(entry?.body ?? '');
  const [on, setOn] = useState(entry?.happenedOn ?? easternToday());
  const [at, setAt] = useState(entry?.happenedAt ?? '');
  const [forId, setFor] = useState(entry?.step?.id ?? step.id);
  const [shared, setShared] = useState(entry?.shared ?? false);
  const [amount, setAmount] = useState<number | null>(entry?.amountCents ?? null);
  const [from, setFrom] = useState(entry?.whoFrom ?? '');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const priced = HAS_AMOUNT.includes(kind);

  const done = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      // The step's log, its history, the timeline and Files all read it.
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      if (!entry) {
        setTitle('');
        setBody('');
        setFiles([]);
        setAmount(null);
        setFrom('');
      }
      onDone();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const save = () =>
    done(async () => {
      const b = {
        stepId: forId,
        happenedOn: on,
        happenedAt: at || null,
        kind,
        title: title.trim(),
        body: body.trim() || null,
        shared,
        amountCents: priced ? amount : null,
        whoFrom: priced ? from.trim() || null : null,
      };
      if (entry) return api(`/projects/${projectId}/history/${entry.id}`, { method: 'PATCH', body: b });
      // Its files up first, under this step's log, shared as it is; then the entry with them.
      const fileIds: string[] = [];
      for (const f of files) {
        const up = await upload<{ id: string; name: string }>(
          `/projects/${projectId}/process/steps/${step.id}/log/files?name=${encodeURIComponent(f.name)}${shared ? '&shared=true' : ''}`,
          f,
          f.type || 'application/octet-stream',
          { notice: false },
        );
        fileIds.push(up.id);
      }
      return api(`/projects/${projectId}/history`, { body: { ...b, fileIds } });
    });

  // An entry changed in place, not saved, on the bar at the window's foot (P-29), as the Timeline's form; one written
  // down for the first time is a new thing made, as Add to the log says.
  const now = { kind, title, body, on, at, forId, shared, amount, from };
  useUnsaved({
    names: entry
      ? changedLabels(
          { kind: entry.kind, title: entry.title, body: entry.body ?? '', on: entry.happenedOn, at: entry.happenedAt ?? '', forId: entry.step?.id ?? step.id, shared: entry.shared, amount: entry.amountCents, from: entry.whoFrom ?? '' },
          now,
          [
            ['What it was', (x) => x.kind],
            ['What happened', (x) => x.title],
            ['The detail', (x) => x.body],
            ['Amount', (x) => x.amount],
            ['From', (x) => x.from],
            ['The day', (x) => x.on],
            ['The time', (x) => x.at],
            ['For', (x) => x.forId],
            ['Shared with the client', (x) => x.shared],
          ],
        )
      : [],
    busy,
    canSave: Boolean(title.trim() && on),
    error: err,
    onSave: save,
    onDiscard: () => {
      if (!entry) return;
      setKind(entry.kind);
      setTitle(entry.title);
      setBody(entry.body ?? '');
      setOn(entry.happenedOn);
      setAt(entry.happenedAt ?? '');
      setFor(entry.step?.id ?? step.id);
      setShared(entry.shared);
      setAmount(entry.amountCents);
      setFrom(entry.whoFrom ?? '');
      setErr(null);
    },
  });

  return (
    <div className={entry ? '' : 'mt-3 grid gap-3 border-t border-rule pt-3'}>
      <div className="grid gap-2">
        <Seg value={kind} options={HISTORY_KINDS} words={(k) => HISTORY_KIND_WORDS[k]} onPick={(k) => k && setKind(k)} label="What it was" keep />
        <label className="label block" htmlFor={`${id}-what`}>
          What happened
        </label>
        <TextInput id={`${id}-what`} value={title} maxLength={300} onChange={(e) => setTitle(e.target.value)} placeholder="One line, as it would read on the timeline" />
        <label className="label block" htmlFor={`${id}-detail`}>
          The detail
        </label>
        <textarea id={`${id}-detail`} className="field resize-y text-sm" rows={2} value={body} maxLength={8000} onChange={(e) => setBody(e.target.value)} placeholder="Optional: who, what was said, what was agreed" />
      </div>
      {priced ? (
        <div className="grid gap-2 sm:grid-cols-2">
          <div>
            <span className="label block">Amount</span>
            <MoneyInput aria-label="Amount" value={amount} onChange={setAmount} />
          </div>
          <div>
            <label className="label block" htmlFor={`${id}-from`}>
              From
            </label>
            <TextInput id={`${id}-from`} aria-label="From" value={from} maxLength={200} onChange={(e) => setFrom(e.target.value)} placeholder="Who it is from" />
          </div>
        </div>
      ) : null}
      {entry ? (
        entry.files.length ? (
          <p className="meta">
            Its files:
            {entry.files.map((f) => (
              <FileChip key={f.id} projectId={projectId} file={f} />
            ))}
          </p>
        ) : null
      ) : (
        <div>
          <DropZone compact label="Drop files here, or choose them" onFiles={(more) => setFiles((was) => [...was, ...more])} busy={busy} />
          {files.length ? (
            <ul className="mt-1 grid gap-1 text-sm">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0 truncate">{f.name}</span>
                  <button type="button" className="link hit text-ink-3" onClick={() => setFiles((was) => was.filter((_, j) => j !== i))}>
                    Take it off
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
      <div className="grid gap-2 sm:grid-cols-3">
        <div>
          <label className="label block" htmlFor={`${id}-on`}>
            The day
          </label>
          <TextInput id={`${id}-on`} type="date" max={easternToday()} value={on} onChange={(e) => setOn(e.target.value)} />
        </div>
        <div>
          <label className="label block" htmlFor={`${id}-at`}>
            The time
          </label>
          <TextInput id={`${id}-at`} type="time" value={at} onChange={(e) => setAt(e.target.value)} />
        </div>
        <div>
          <label className="label block" htmlFor={`${id}-for`}>
            For
          </label>
          <select id={`${id}-for`} className="field" value={forId} onChange={(e) => setFor(e.target.value)}>
            <option value={step.id}>The whole step</option>
            {tasks.map((t) => (
              <option key={t.id} value={t.id}>
                {titleCase(t.title)}
              </option>
            ))}
          </select>
        </div>
      </div>
      <Toggle checked={shared} onChange={setShared} label="Shared with the client" hint={shared ? 'They read it, and its files, on their step.' : 'NSB’s alone.'} />
      <div className="flex flex-wrap items-center gap-4">
        <Button busy={busy} disabled={!title.trim() || !on} onClick={() => void save()}>
          {entry ? 'Save' : 'Add to the log'}
        </Button>
        {entry ? (
          <>
            <Button tone="quiet" onClick={onDone}>
              Cancel
            </Button>
            {withdrawing ? (
              <span className="text-sm">
                Take it off the timeline? It is kept, with who withdrew it.{' '}
                <button type="button" className="link hit text-alert" onClick={() => void done(() => api(`/projects/${projectId}/history/${entry.id}/withdraw`, { body: {} }))}>
                  Withdraw
                </button>{' '}
                <button type="button" className="link hit" onClick={() => setWithdrawing(false)}>
                  Keep it
                </button>
              </span>
            ) : (
              <button type="button" className="link hit text-ink-3" onClick={() => setWithdrawing(true)}>
                Withdraw it
              </button>
            )}
          </>
        ) : null}
      </div>
      <ErrorLine error={err} />
    </div>
  );
}

